import styled, {css} from "@yb/styled-px2rem";
import React, {useEffect, useRef, useState} from "react";
import {PopupCloseButton} from "@FRAMEWORK/ui/mobile/button";
import {PopupProps} from "@FRAMEWORK/ui/types/d";

interface Props extends PopupProps {
	size?: "basic" | "full";
	padding?: boolean;
	close?: () => void;
	show?: boolean;
	animate?: "basic" | "slideIn";
	icon?: React.ReactNode;
}

const Wrap = styled.div<{animate?: "basic" | "slideIn"; show?: boolean; size?: "basic" | "full"}>`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	z-index: 101;
	display: flex;
	align-items: ${props => {
		if (props.animate === "slideIn") {
			return "flex-end";
		}
		if (props.animate === "basic") {
			return "flex-start";
		}
	}};
	justify-content: center;
	width: 100%;
	height: 100%;
	padding-top: ${props => props.size === "basic" && "20px"};
	background-color: rgba(0, 0, 0, 0.6);
	transition: all 0.3s ease;
	opacity: 0;
	visibility: ${props => (props.show ? "visible" : "hidden")};
	transition: visibility 0.3s ease-out;

	animation: ${props =>
		props.show
			? css`
				fadeIn 0.3s ease forwards
			`
			: css`
				fadeOut 0.3s ease forwards
			`};

	@keyframes fadeIn {
		0% {
			opacity: 0;
		}
		100% {
			opacity: 1;
		}
	}

	@keyframes fadeOut {
		0% {
			opacity: 1;
		}
		100% {
			opacity: 0;
		}
	}
`;

const Content = styled.div<{size: "basic" | "full"; animate?: "basic" | "slideIn"; show?: boolean}>`
	position: relative;
	z-index: 105;
	display: flex;
	flex-direction: column;
	background-color: #fff;
	animation: ${props => {
		if (props.animate === "slideIn" && props.show) {
			return `
				slideIn 0.3s cubic-bezier(0, 0, 0.2, 1)
			`;
		}
		if (props.animate === "slideIn" && !props.show) {
			return `
				slideOut 0.3s cubic-bezier(0, 0, 0.2, 1)
			`;
		}
	}};

	${props => {
		if (props.size === "basic") {
			return css`
				flex: 0 0 auto;
				width: 90%;
				max-height: 90%;
				overflow: hidden auto;
			`;
		}
		if (props.size === "full") {
			return css`
				flex: 1 1 auto;
				width: 100%;
				height: 100%;
				overflow-y: scroll;
				-webkit-overflow-scrolling: touch;
			`;
		}
		if (props.size) {
			return css`
				flex: 1 1 auto;
				width: 100%;
				height: auto;
				max-height: 320px;
				border-top-left-radius: 12px;
				border-top-right-radius: 12px;
				overflow-y: scroll;
			`;
		}
	}}

	@keyframes slideIn {
		0% {
			transform: translateY(100%);
		}
		100% {
			transform: translateY(0);
		}
	}

	@keyframes slideOut {
		0% {
			transform: translateY(0);
		}
		100% {
			transform: translateY(100%);
		}
	}
`;

const Header = styled.div`
	position: sticky;
	top: 0;
	z-index: 101;
	display: flex;
	justify-content: center;
	align-items: center;
	flex: 0 0 60px;
	height: 60px;
	padding: 0 15px;
	border-bottom: 1px solid var(--line-color);
	background: #fff;
	> button {
		position: absolute;
		top: 50%;
		right: 15px;
		transform: translateY(-50%);
	}
`;

const Container = styled.div<{padding: boolean}>`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	padding: ${props => (props.padding ? "30px 15px" : "0")};
`;

const Title = styled.h3`
	display: -webkit-box;
	font-size: 21px;
	letter-spacing: -0.05em;
	color: var(--emp-color);
	overflow: hidden;
	font-weight: bold;
	text-overflow: ellipsis;
	white-space: normal;
	word-wrap: break-word;
	word-break: break-all;
	-webkit-line-clamp: 1;
	-webkit-box-orient: vertical;
	line-clamp: 1;
`;

const PopupContainer = ({
	title = "알림",
	children,
	size = "basic",
	padding = true,
	close,
	show = true,
	animate = "basic",
	icon,
}: Props) => {
	const wrapRef = useRef<HTMLDivElement>(null);
	const body = document.querySelector("body") as HTMLElement;
	const [scrollY, setScrollY] = useState(window.scrollY);

	useEffect(() => {
		if (show) {
			body.style.position = "fixed";
			body.style.top = `-${scrollY}px`;
			body.style.left = "0";
			body.style.right = "0";
		}
		return () => {
			const showCount = document.querySelectorAll("[data-show='true']")?.length || 0;
			if (showCount > 0) return;
			body.style.removeProperty("position");
			body.style.removeProperty("top");
			body.style.removeProperty("left");
			body.style.removeProperty("right");

			window.scrollTo(0, scrollY);
			setScrollY(window.scrollY);
		};
	}, [show]);

	return (
		<Wrap ref={wrapRef} data-show={String(show)} show={show} animate={animate} size={size}>
			<Content size={size} animate={animate} show={show}>
				<Header>
					{icon}
					<Title>{title}</Title>
					<PopupCloseButton onClose={close} />
				</Header>
				<Container padding={padding}>{children}</Container>
			</Content>
		</Wrap>
	);
};

export default PopupContainer;
